/**
 * Commission reports API client. Talks to the real backend.
 *
 * Endpoints are org-scoped: the caller supplies the org UUID, which lives
 * in the auth state from the login response. Once the backend moves to
 * pulling org context from the JWT, this module will drop the orgUuid
 * argument and the paths will shorten.
 */
import api from './client';

const base = (orgUuid) => `/organisations/${orgUuid}/commission-runs`;

export const commissionReportsApi = {
    listYears(orgUuid) {
        return api.get(`${base(orgUuid)}/years`).then((r) => r.data);
    },

    listMonths(orgUuid, year) {
        return api.get(base(orgUuid), { params: { year } }).then((r) => r.data);
    },

    getMonth(orgUuid, year, month) {
        return api.get(`${base(orgUuid)}/${year}/${month}`).then((r) => r.data);
    },

    /**
     * Upload one input workbook for a month. Auto-creates the run record on
     * the server side if this is the first upload for that month.
     *
     * The workbook is converted here, in the browser, into one CSV per sheet
     * the engine reads — from the stored cell values, never the displayed
     * text — and the server receives the workbook (kept unopened, for
     * download), the CSVs, and a manifest of what each one holds. See
     * lib/workbookToCsv.js for why.
     *
     * @param file native File from an <input type="file"> change event
     * @param kind discriminator string; must match a value in
     *   {@link com.commissioner.service.CommissionRunService} —
     *   one of 'CRM', 'ADHOC', 'LENDER_UPFRONT', 'LENDER_TRAIL'.
     * @param onProgress optional (loaded, total) => void called during upload
     * @throws WorkbookConversionError before anything is sent, when the file
     *   is not a workbook or lacks a sheet the kind needs; its message is
     *   written for the person who chose the file
     */
    async uploadInputFile(orgUuid, year, month, file, kind, onProgress) {
        // The workbook reader is the largest thing in the bundle and is only
        // needed at the moment of an upload, so it is loaded then.
        const { convertWorkbook } = await import('../lib/workbookToCsv');
        const converted = convertWorkbook(await file.arrayBuffer(), kind);

        const formData = new FormData();
        formData.append('original', file, file.name);
        for (const [part, { csv }] of Object.entries(converted.parts)) {
            formData.append(part, new Blob([csv], { type: 'text/csv' }), `${part}.csv`);
        }
        formData.append('manifest', JSON.stringify(converted.manifest));

        return api
            .post(`${base(orgUuid)}/${year}/${month}/input-files`, formData, {
                params: { kind },
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (onProgress && e.total) onProgress(e.loaded, e.total);
                },
            })
            .then((r) => r.data);
    },

    /**
     * Email a generated RCTI to the payee it is addressed to, with the
     * workbook attached. The server sends to the address saved on the
     * payee's business entity — there is no address to supply here — and
     * answers 400 with a message naming the payee when none is saved.
     *
     * @returns {{fileId, recipientName, sentTo, sentAt}}
     */
    emailRcti(orgUuid, year, month, fileId) {
        return api
            .post(`${base(orgUuid)}/${year}/${month}/files/${fileId}/email`)
            .then((r) => r.data);
    },

    deleteInputFile(orgUuid, year, month, fileId) {
        return api
            .delete(`${base(orgUuid)}/${year}/${month}/input-files/${fileId}`)
            .then((r) => r.data);
    },

    /**
     * Download a file (input or output) for the given month, streamed
     * back through the application — bytes never leave the API
     * boundary, no presigned S3 URLs exposed to the browser. Same auth
     * rules as every other endpoint.
     *
     * <p>Returns the axios response so the caller can read both the
     * blob and the {@code Content-Disposition} header (which carries
     * the canonical filename). The browser's {@code download} attribute
     * picks up that filename when we trigger the save via an anchor
     * click.
     */
    downloadFile(orgUuid, year, month, fileId) {
        return api.get(
            `${base(orgUuid)}/${year}/${month}/files/${fileId}/download`,
            { responseType: 'blob' });
    },

    /**
     * Execute the commission engine for the given month. Pulls the input
     * files from S3, runs the calc, writes the output xlsx back to S3,
     * and returns the updated run state (status flipped to COMPLETED or
     * FAILED, with the new output file in `outputFiles`).
     *
     * <p>Backend returns 202 Accepted — execution is synchronous today
     * but the response is treated as if it were async, so callers should
     * inspect `response.status` (not the HTTP code) to decide what
     * happened. A COMPLETED response means the output file is ready to
     * download immediately; a FAILED response carries a `failureReason`.
     *
     * <p>Rates are not sent per-run. The backend uses the organisation's
     * saved rate config (set under Configuration → Rates). If no config
     * has been saved, the backend responds 400 with a message.
     */
    executeRun(orgUuid, year, month) {
        // Processing a full month parses a multi-MB CRM workbook and can take
        // a minute or more. The client-wide 30s timeout used to give up while
        // the server kept working — users clicked again and two overlapping
        // runs collided. 5 minutes, and never retry this call automatically.
        return api
            .post(`${base(orgUuid)}/${year}/${month}/execute`, undefined, { timeout: 300000 })
            .then((r) => r.data);
    },

    /**
     * Record the split rates and payee names of the loans in this month's CRM,
     * WITHOUT running the commission engine.
     *
     * <p>Backs the amber "Import rate splits" action on the CRM row. Seeds the
     * loan rate store for a book that predates the system: a loan's rates and
     * payees appear in the CRM only in the month it settled, so once it drops out
     * the store is the only source for them. Upload the historical CRM under its
     * own month and call this.
     *
     * <p>Two later operations depend on it — pricing TRAIL in months after the
     * loan leaves the CRM, and reversing a CLAWBACK at the upfront splits the loan
     * was originally paid at. A discharge lands months after settlement, so a
     * clawed-back loan is essentially never in the current CRM; with nothing
     * stored, the reclaim is reported unmatched and nothing is recovered.
     *
     * <p>Needs the CRM alone — no Adhoc or lender files. Produces no reports,
     * no RCTIs, and does NOT change the month's status: a seeded month stays
     * Pending, because no commission was calculated for it.
     *
     * <p>Safe to call repeatedly; a loan already stored is restated in place.
     * Oldest-first is the recommended order but not required.
     *
     * @returns { year, month, loansInCrm, ratesInserted, ratesUpdated,
     *            ratesBackdated, loansStored, runStatus }
     */
    populateLoanRates(orgUuid, year, month) {
        return api
            .post(`${base(orgUuid)}/${year}/${month}/populate-loan-rates`)
            .then((r) => r.data);
    },

    /**
     * Discover payees and their offices named in the month's uploaded CRM
     * that aren't yet in the org's config. Read-only — creates nothing.
     *
     * <p>Returns { subbyOffices, referrerOffices }, each an array of offices
     * carrying their payees. Every office and payee has a `verdict`
     * ('EXISTING' | 'FUZZY_SUGGESTION' | 'NEW'); fuzzy items also carry
     * `fuzzyCandidate` (the resembled existing name) and `fuzzyScore`.
     *
     * <p>404 if no run/CRM exists for the month; 400 if the CRM can't be
     * read (message names the missing column).
     */
    discoverPayees(orgUuid, year, month) {
        return api
            .get(`${base(orgUuid)}/${year}/${month}/payee-suggestions`)
            .then((r) => r.data);
    },

    /**
     * Act on the user's discovery decisions: create the approved offices and
     * payees, skip the rest. Partial-success by design — an item that
     * collides with an existing record is skipped and reported, not failed.
     *
     * @param confirmation {
     *   offices: [{
     *     action: 'CREATE' | 'SKIP',
     *     existingUuid,            // required for SKIP offices that have CREATE payees
     *     name, type, abn, contactName, contactEmail, contactPhone, address,
     *     payees: [{
     *       action: 'CREATE' | 'SKIP',
     *       type, name, code, contactName, contactEmail, contactPhone
     *     }]
     *   }]
     * }
     * @returns { createdOffices: [{name,uuid}], createdPayees: [{name,uuid}],
     *            skipped: [{kind,name,reason}] }
     */
    confirmPayees(orgUuid, year, month, confirmation) {
        return api
            .post(`${base(orgUuid)}/${year}/${month}/payee-suggestions/confirm`, confirmation)
            .then((r) => r.data);
    },

    /**
     * Delete all run data for a month: the commission-run record, its input
     * and output files (from S3), and the payee-payment-summary rows recorded
     * for that run. Does NOT delete configuration (subcontractors, referrers,
     * products, rates) — only the run and what it produced.
     *
     * <p>Used when a run was generated for the wrong month (e.g. CRM rows
     * whose Settled Date falls outside the run month): clean it here, then
     * re-upload and run under the correct month.
     *
     * <p>Returns 204 No Content on success; 404 if no run exists for the month.
     */
    cleanRunData(orgUuid, year, month) {
        return api
            .delete(`${base(orgUuid)}/${year}/${month}`)
            .then((r) => r.data);
    },
};